import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { cn } from '@/shared/cn';

const VIEWPORT_PAD = 8;

type ChromeDropLeftProps = {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  role?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'style' | 'role'>;

// Flyout anchored to the left of a vertical-chrome trigger. Flips to grow
// upward when the menu would clip below the viewport (common for lower icons).
export function ChromeDropLeft({
  open,
  anchorRef,
  className,
  style,
  children,
  role = 'menu',
  ...rest
}: ChromeDropLeftProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [flipUp, setFlipUp] = useState(false);

  useLayoutEffect(() => {
    if (!open) return;

    const apply = () => {
      const menu = menuRef.current;
      const anchor = anchorRef.current;
      if (!menu || !anchor) return;

      const menuHeight = menu.offsetHeight;
      const { top, bottom } = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - top - VIEWPORT_PAD;
      const spaceAbove = bottom - VIEWPORT_PAD;
      const shouldFlip = menuHeight > spaceBelow && spaceAbove >= spaceBelow;
      setFlipUp(shouldFlip);
    };

    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [open, anchorRef, children]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      className={cn('chrome-drop-left glass-deep', flipUp && 'chrome-drop-left--up', className)}
      style={{
        maxHeight: `calc(100vh - ${VIEWPORT_PAD * 2}px)`,
        overflowY: 'auto',
        ...style,
      }}
      role={role}
      {...rest}
    >
      {children}
    </div>
  );
}
